import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger,
} from './ui/sheet';
import { ScrollArea } from './ui/scroll-area';
import { Library, Loader2, Languages } from 'lucide-react';
import useTranslate from '../hooks/useTranslate';
import { usePremiumGate } from '../hooks/usePremiumGate';
import NowFreeBadge, { markNowFreeSeen } from './NowFreeBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * JFB Commentary trigger — sibling to MatthewHenryButton. Opens
 * Jamieson-Fausset-Brown's classic 1871 verse-by-verse commentary in a
 * slide-in sheet for the Bible reader's current book + chapter.
 *
 * Backend: `GET /api/resources/jfb/{book}/{chapter}` returns a single
 * doc with a `text` field — usually 8-40k characters of commentary on the
 * chapter. We render it as preformatted paragraphs.
 */
const JfbCommentaryButton = ({ book, chapter, language = 'en' }) => {
  const [open, setOpen] = useState(false);
  const { dialog: premiumDialog } = usePremiumGate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !book || !chapter) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.get(`${BACKEND_URL}/api/resources/jfb/${encodeURIComponent(book)}/${chapter}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (cancelled) return;
        const msg = e?.response?.status === 404
          ? (language === 'es'
            ? 'Comentario no disponible para este capítulo'
            : 'Commentary not available for this chapter')
          : (language === 'es' ? 'Error al cargar' : 'Failed to load');
        setError(msg);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, book, chapter, language]);

  const { output: tText, loading: tLoading, showOriginal, setShowOriginal, isTranslated } =
    useTranslate([data?.text || ''], language);

  const titleEn = 'JFB · Commentary';
  const titleEs = 'JFB · Comentario';

  return (
    <>
    <Sheet open={open} onOpenChange={(v) => { if (v) markNowFreeSeen('jfb'); setOpen(v); }}>
      <SheetTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="h-8 px-2.5 text-xs gap-1.5 border border-emerald-300/50 bg-emerald-50/50 hover:bg-emerald-100/70 text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200 dark:border-emerald-800/50 dark:hover:bg-emerald-900/50"
          data-testid="jfb-open-btn"
          title={language === 'es' ? titleEs : titleEn}
        >
          <Library className="w-3.5 h-3.5" />
          <span className="hidden sm:inline font-medium">JFB</span>
          <NowFreeBadge seenKey="jfb" language={language} />
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl overflow-hidden flex flex-col p-0"
        data-testid="jfb-sheet"
      >
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-emerald-200/60 dark:border-emerald-800/40">
          <SheetTitle className="text-emerald-900 dark:text-emerald-100 flex items-center gap-2">
            <Library className="w-5 h-5" />
            {language === 'es' ? titleEs : titleEn}
          </SheetTitle>
          <SheetDescription className="text-xs">
            {book} {chapter}
            {' · '}
            {language === 'es'
              ? 'Jamieson-Fausset-Brown · Dominio público (1871)'
              : 'Jamieson-Fausset-Brown · Public Domain (1871)'}
          </SheetDescription>
          {isTranslated && data && (
            <Button
              onClick={() => setShowOriginal((v) => !v)}
              size="sm"
              variant="ghost"
              className="h-7 px-2 mt-1 w-fit text-[11px] font-semibold text-emerald-800 hover:bg-emerald-100/60 dark:text-emerald-200 dark:hover:bg-emerald-900/40"
              data-testid="jfb-translate-toggle"
              disabled={tLoading}
            >
              {tLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Languages className="w-3 h-3 mr-1" />}
              {showOriginal ? 'Ver traducción' : 'Ver original'}
            </Button>
          )}
        </SheetHeader>
        <ScrollArea className="flex-1 px-5 py-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
            </div>
          )}
          {error && !loading && (
            <p className="text-sm text-slate-600 dark:text-slate-400 italic">{error}</p>
          )}
          {data && !loading && (
            <div className="space-y-3 text-sm leading-relaxed whitespace-pre-line text-slate-800 dark:text-slate-200">
              {tText[0]}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
    {premiumDialog}
    </>
  );
};

export default JfbCommentaryButton;
